import * as React from "react";

import { useMediaQuery } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";

export interface ResponsiveDataTableColumn<T> {
  key: string;
  header: React.ReactNode;
  render: (row: T) => React.ReactNode;
  align?: "left" | "right";
}

export interface ResponsiveDataTableMobileField<T> {
  label: React.ReactNode;
  render: (row: T) => React.ReactNode;
}

export interface ResponsiveDataTableRowControls {
  canExpand: boolean;
  expansionId: string;
  isExpanded: boolean;
  mode: "desktop" | "mobile";
  toggleExpanded: () => void;
}

export interface ResponsiveDataTableProps<T> {
  rows: T[];
  isLoading?: boolean;
  loadingTitle?: React.ReactNode;
  error?: React.ReactNode;
  emptyTitle?: React.ReactNode;
  emptyDescription?: React.ReactNode;
  desktopHeader?: React.ReactNode;
  getRowKey?: (row: T, index: number) => string;
  renderDesktopRow: (
    row: T,
    controls?: ResponsiveDataTableRowControls,
  ) => React.ReactNode;
  renderMobileRow: (
    row: T,
    controls?: ResponsiveDataTableRowControls,
  ) => React.ReactNode;
  renderExpandedRow?: (
    row: T,
    controls?: ResponsiveDataTableRowControls,
  ) => React.ReactNode;
  pagination?: React.ReactNode;
  className?: string;
}

function StatePanel({
  title,
  description,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
}) {
  return (
    <div className="px-4 py-8 text-center">
      <p className="text-foreground text-sm font-medium">{title}</p>
      {description ? (
        <p className="text-muted-foreground mt-1 text-sm">{description}</p>
      ) : null}
    </div>
  );
}

function toSafeRowKey(rowKey: string) {
  return rowKey.replace(/[^a-zA-Z0-9_-]/g, "_");
}

export function ResponsiveDataTable<T>({
  rows,
  isLoading = false,
  loadingTitle = "Carregando dados...",
  error,
  emptyTitle = "Nenhum registro encontrado",
  emptyDescription,
  desktopHeader,
  getRowKey,
  renderDesktopRow,
  renderMobileRow,
  renderExpandedRow,
  pagination,
  className,
}: ResponsiveDataTableProps<T>) {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [expandedRowKeys, setExpandedRowKeys] = React.useState<Set<string>>(
    () => new Set(),
  );
  const canExpandRows = Boolean(renderExpandedRow && getRowKey);

  function getRowKeyForIndex(row: T, index: number) {
    return getRowKey ? getRowKey(row, index) : String(index);
  }

  function getRowControls(row: T, index: number, mode: "desktop" | "mobile") {
    const rowKey = getRowKeyForIndex(row, index);
    const expansionId = `responsive-data-table-expansion-${toSafeRowKey(rowKey)}`;
    const isExpanded = canExpandRows && expandedRowKeys.has(rowKey);

    return {
      canExpand: canExpandRows,
      expansionId,
      isExpanded,
      mode,
      toggleExpanded: () => {
        if (!canExpandRows) {
          return;
        }

        setExpandedRowKeys((current) => {
          const next = new Set(current);

          if (next.has(rowKey)) {
            next.delete(rowKey);
          } else {
            next.add(rowKey);
          }

          return next;
        });
      },
    };
  }

  return (
    <section
      className={cn(
        "bg-card text-card-foreground overflow-hidden rounded-xl border shadow-sm",
        className,
      )}
    >
      {isLoading ? (
        <StatePanel title={loadingTitle} />
      ) : error ? (
        <StatePanel
          title="Não foi possível carregar os dados"
          description={error}
        />
      ) : rows.length === 0 ? (
        <StatePanel title={emptyTitle} description={emptyDescription} />
      ) : isDesktop ? (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            {desktopHeader ? <thead>{desktopHeader}</thead> : null}
            <tbody>
              {rows.map((row, index) => {
                const controls = getRowControls(row, index, "desktop");

                return (
                  <React.Fragment key={getRowKeyForIndex(row, index)}>
                    {renderDesktopRow(row, controls)}
                    {controls.isExpanded && renderExpandedRow
                      ? renderExpandedRow(row, controls)
                      : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="divide-y">
          {rows.map((row, index) => {
            const controls = getRowControls(row, index, "mobile");

            return (
              <React.Fragment key={getRowKeyForIndex(row, index)}>
                {renderMobileRow(row, controls)}
                {controls.isExpanded && renderExpandedRow
                  ? renderExpandedRow(row, controls)
                  : null}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {pagination && rows.length > 0 ? <div>{pagination}</div> : null}
    </section>
  );
}
